import Details from '@/components/Details/Details';
import Tip from '@/components/Tip/Tip';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import HeaderTemplate from '@/templates/Header.template';
import SectionSchedule from '@/templates/booking-rules/section-schedule/section-schedule';
import {
	Box,
	Button,
	Flex,
	Heading,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Skeleton,
	Stack,
	Text,
	Tooltip,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { FcServices } from 'react-icons/fc';
import { FiCopy, FiEye, FiLock, FiMoon, FiSettings, FiSun, FiTrash, FiUnlock } from 'react-icons/fi';
import { VscChevronLeft } from 'react-icons/vsc';
import { Link } from 'react-router-dom';
import SectionAppearance from './components/SectionAppearance/SectionAppearance';
import SectionCalendars from './components/SectionCalendars/SectionCalendars';
import SectionGeneral from './components/SectionGeneral/SectionGeneral';
import SectionHosts from './components/SectionHosts/SectionHosts';
import SectionLimits from './components/SectionLimits/SectionLimits';
import SectionNotifications from './components/SectionNotifications/SectionNotifications';
import SectionPto from './components/SectionPto/SectionPto';
import SectionQuestions from './components/SectionQuestions/SectionQuestions';
import SectionShare from './components/SectionShare/SectionShare';
import SectionParticipants from './components/section-participants/section-participants.view';
import SectionPayments from './components/section-payments/section-payments.view';
import SectionWorkflows from './components/section-workflows/section-workflows.view';
import useServiceForm from './useEventTypeForm';

interface IEventTypeFormSuccessProps {
	isPreviewEnabled: boolean;
	togglePreview: () => void;
}

export default function EventTypeFormSuccess(props: IEventTypeFormSuccessProps) {
	const {
		copyLink,
		deleteEventType,
		duplicateEventType,
		handleChange,
		handleLockClick,
		handleOpenPreviewClick,
		handleTurnOffClick,
		handleTurnOnClick,
		handleUnLockClick,
		deprecatedOptionsDisclosure,
		eventType,
		queryEdit,
	} = useServiceForm();

	return (
		<Box
			overflow="auto"
			display={{
				base: props.isPreviewEnabled ? 'none' : 'grid',
				md: 'grid',
			}}
			transition="width .3s"
			maxW={{ base: '100%', md: 'md', lg: 'lg', xl: 'lg' }}
			minW={{ base: '100%', md: 'md', lg: 'lg', xl: 'lg' }}
			gridTemplateRows="auto 1fr"
		>
			<Box pt={4} px={4}>
				<HeaderTemplate
					left={
						<Button as={Link} to={ROUTE_DASHBOARD} size="sm" leftIcon={<VscChevronLeft />}>
							{t('Back')}
						</Button>
					}
					center={eventType?.name}
					right={
						<Menu>
							<MenuButton size="sm" as={Button} rightIcon={<BiDotsVerticalRounded />}>
								{t('Menu')}
							</MenuButton>
							<MenuList>
								<MenuItem icon={<FiEye />} disabled={props.isPreviewEnabled} onClick={handleOpenPreviewClick}>
									{t('Preview')}
								</MenuItem>

								<MenuItem icon={<FiCopy />} isDisabled={!eventType} onClick={duplicateEventType}>
									{t('Duplicate booking calendar')}
								</MenuItem>

								{eventType?.enabled ? (
									<MenuItem icon={<FiMoon />} isDisabled={!eventType} onClick={handleTurnOffClick}>
										{t('Disable booking calendar')}
									</MenuItem>
								) : (
									<MenuItem icon={<FiSun />} isDisabled={!eventType} onClick={handleTurnOnClick}>
										{t('Enable booking calendar')}
									</MenuItem>
								)}

								{eventType?.tags.ttLocked ? (
									<MenuItem icon={<FiUnlock />} isDisabled={!eventType} onClick={handleUnLockClick}>
										{t('Unlock booking calendar')}
									</MenuItem>
								) : (
									<MenuItem icon={<FiLock />} isDisabled={!eventType} onClick={handleLockClick}>
										{t('Lock booking calendar')}
									</MenuItem>
								)}

								<MenuItem icon={<FiSettings />} isDisabled={!eventType} onClick={deprecatedOptionsDisclosure.onToggle}>
									{t('Show deprecated options')}
								</MenuItem>
								<MenuDivider />
								<MenuItem icon={<FiTrash />} isDisabled={!eventType} onClick={deleteEventType}>
									{t('Delete')}
								</MenuItem>
							</MenuList>
						</Menu>
					}
				/>
			</Box>

			{eventType ? (
				eventType.tags.ttLocked ? (
					<Box px={4} py={8} display="grid" justifyItems="center" gridTemplateRows="auto auto 1fr" gap={4}>
						<Box>
							<FiLock size={30} />
						</Box>

						<Heading textAlign="center" size="sm">
							{t('This booking calendar was locked')}
						</Heading>

						<Box mt="auto">
							<Tip>
								{t(
									'Blocking a reservation calendar is used to prevent unexpected changes in availability or in its settings. If you want to make changes, unlock it.'
								)}
							</Tip>
						</Box>

						<Button onClick={handleUnLockClick} w="full" colorScheme="primary">
							{t('Unlock booking calendar')}
						</Button>
					</Box>
				) : !eventType.enabled ? (
					<Box px={4} py={8} display="grid" justifyItems="center" gridTemplateRows="auto auto 1fr" gap={4}>
						<Box>
							<FiMoon size={32} />
						</Box>

						<Heading mt={4} size="sm">
							{t('This booking calendar is disabled.')}
						</Heading>

						<Button mt="auto" onClick={handleTurnOnClick} w="full" colorScheme="primary">
							{t('Enable booking calendar')}
						</Button>
					</Box>
				) : (
					<Flex flex="1" flexDir="column" h="100%" overflow="auto" px="4">
						<SectionGeneral service={eventType} onChange={handleChange} />
						<SectionAppearance service={eventType} onChange={handleChange} isLoading={queryEdit.isLoading} />
						<SectionQuestions service={eventType} onChange={handleChange} />
						<SectionParticipants service={eventType} onChange={handleChange} />
						<SectionWorkflows />
						<SectionPayments service={eventType} onChange={handleChange} />

						{deprecatedOptionsDisclosure.isOpen ? (
							<Tooltip
								label={t(
									'It is recommended to create an availability and link it to the booking calendar instead of creating a schedule.'
								)}
							>
								<Details title={t('Other options')} image={<FcServices />}>
									<Box bg="red.50" p={4} mb={2} borderRadius="md" borderColor="red.200" borderWidth={1}>
										<Text color="red.900">
											{t('Please use the options on the availabilities & participants section instead this.')}
										</Text>
									</Box>
									<SectionSchedule
										bookingRules={{
											checkAvailabilityInCalendars: [],
											repeatingAvailability: eventType?.repeatingAvailability,
										}}
										onChange={(value) => handleChange(value)}
									/>

									<SectionCalendars isLoading={queryEdit.isLoading} service={eventType} onChange={handleChange} />
									<SectionLimits service={eventType} onChange={handleChange} />
									<SectionPto service={eventType} onChange={handleChange} />
									<SectionHosts service={eventType} onChange={handleChange} />
									<SectionNotifications service={eventType} onChange={handleChange} />
								</Details>
							</Tooltip>
						) : null}
						<SectionShare service={eventType} onChange={handleChange} />

						<Box mt="auto" pb="8" pt="4">
							<Button onClick={() => copyLink(eventType)} w="100%" colorScheme="primary">
								{t('Copy link')}
							</Button>
						</Box>
					</Flex>
				)
			) : (
				<Stack padding="0 1.3rem" spacing="1px">
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
				</Stack>
			)}
		</Box>
	);
}
